import {
	AcquirerType,
	IAdyen,
	IRestaurantBankSettings,
	IRestaurantBankSettingsAcquirer,
	IUpdateRestaurantBankSettings,
	TYandexPaymentType,
	IExtendedRestaurantBankSettings,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/editRestaurantState/editRestaurantContext';
import { BankSettingsContext, bankSettingsInitialState, TTypeOfModalWindow } from './bankSettingsContext';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { bankSettingsReducer } from './bankSettingsReducer';
import { BankSettingsActionsTypes } from './bankSettingsActions';
import { getRestaurantBankSettings } from 'backend/restaurants';

const normalizeAdyenSettings = (adyen: Nullable<IAdyen>): IAdyen => ({
	merchantAccount: null,
	store: null,
	apiKey: null,
	checkoutUrlPrefix: null,
	splitPayments3S: false,
	merchantBalanceAccountId: null,
	kmBalanceAccountId: null,
	...adyen,
});

const normalizeGenerateQrInstantly = (bankSettings: IRestaurantBankSettings[]) => {
	const activeQrSettingsId = bankSettings.find(
		(el) => el.acquirer?.type === AcquirerType.QR && el.generateQrInstantly,
	)?.id;

	return bankSettings.map((el) => ({ ...el, generateQrInstantly: el.id === activeQrSettingsId }));
};

const BankSettingsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(bankSettingsReducer, bankSettingsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantId, visibleForm } = useContext<IEditRestaurantContext>(EditRestaurantContext);

	const needRequest = visibleForm === 'bankSettingsForm' && restaurantId;

	const { response, refetch, loading } = useFetchData(
		needRequest ? getRestaurantBankSettings : null,
		restaurantId || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: BankSettingsActionsTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		if (response) {
			const normalizedBankSettings = normalizeGenerateQrInstantly(response.data.restaurantBankSettings);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: normalizedBankSettings.map((bankSetting) => ({
						...bankSetting,
						isVivaSecretUpdated: false,
						isYandexChequeSecretUpdated: false,
					})),
				},
			});
			dispatch({
				type: BankSettingsActionsTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						bankSettings: normalizedBankSettings.map((bankSetting) => ({
							...bankSetting,
							isVivaSecretUpdated: false,
							isYandexChequeSecretUpdated: false,
						})),
					},
				},
			});
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [needRequest]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: BankSettingsActionsTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const addBankSettingBlock = useCallback(
		(value: IExtendedRestaurantBankSettings) => {
			const updatedBankSettingsBlocks = [...state.bankSettings, value];

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const deleteBankSettingBlock = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.filter((el) => el.id !== value);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changePriority = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, priority: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeMerchantId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, merchantId: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeMerchantName = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, merchantName: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeAcquirer = useCallback(
		(settingsId: string, value: IRestaurantBankSettingsAcquirer) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							acquirer: value,
							yandexApiKey: null,
							yandexPaymentTypes: [],
							sbpAccount: null,
							sbpUserLogin: null,
							vivaApiKey: null,
							vivaApiSecret: null,
							isVivaSecretUpdated: !!el.vivaApiSecret,
							yandexChequeApiToken: null,
							yandexChequeApiPartnerId: null,
							isYandexChequeSecretUpdated: !!el.yandexChequeApiToken,
							accNumber: null,
							sector: null,
							sdRef: null,
							adyen: null,
							smsCode: null,
							tBankPlaceId: null,
							masterWallet: null,
							generateQrInstantly: value.type === AcquirerType.QR ? el.generateQrInstantly : false,
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeSbpAccountNumber = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, sbpAccount: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeSbpLogin = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, sbpUserLogin: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeYandexApiKey = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, yandexApiKey: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeYandexPaymentType = useCallback(
		(settingsId: string, value: TYandexPaymentType[]) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, yandexPaymentTypes: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeVivaApiKey = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, vivaApiKey: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeVivaApiSecret = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, vivaApiSecret: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeAccNumber = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, accNumber: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeSector = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, sector: value ? Number(value) : null } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeSdRef = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, sdRef: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeYandexChequeApiToken = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, yandexChequeApiToken: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeYandexChequeApiPartnerId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, yandexChequeApiPartnerId: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeMerchantAccount = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), merchantAccount: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeStore = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), store: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeXApiKey = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), apiKey: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeCheckoutUrlPrefix = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), checkoutUrlPrefix: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const toggleSplitPayments3S = useCallback(
		(settingsId: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) => {
				if (el.id !== settingsId) {
					return el;
				}

				const isSplitPaymentsEnabled = !el.adyen?.splitPayments3S;
				const normalizedAdyen = normalizeAdyenSettings(el.adyen);

				const adyen = isSplitPaymentsEnabled
					? { ...normalizedAdyen, splitPayments3S: true }
					: {
							...normalizedAdyen,
							splitPayments3S: false,
							merchantBalanceAccountId: null,
							kmBalanceAccountId: null,
					  };

				return { ...el, adyen };
			});

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeMerchantBalanceAccountId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), merchantBalanceAccountId: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeKmBalanceAccountId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							adyen: { ...normalizeAdyenSettings(el.adyen), kmBalanceAccountId: value },
					  }
					: el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const toggleDisplayTips = useCallback(
		(settingsId: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, isTipsSupported: !el.isTipsSupported } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const toggleIsFeeFiscalized = useCallback(
		(settingsId: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, isFeeFiscalized: !el.isFeeFiscalized } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const toggleGenerateQrInstantly = useCallback(
		(settingsId: string) => {
			const isTurningOn = !state.bankSettings.find((el) => el.id === settingsId)?.generateQrInstantly;

			const updatedBankSettingsBlocks = state.bankSettings.map((el) => ({
				...el,
				generateQrInstantly: isTurningOn && el.id === settingsId,
			}));

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changePaymentTypeId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, paymentTypeId: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeSmsCode = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, smsCode: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeTBankPlaceId = useCallback(
		({ settingsId, value }: { settingsId: string; value: string }) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, tBankPlaceId: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeMasterWallet = useCallback(
		({ settingsId, value }: { settingsId: string; value: string }) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, masterWallet: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const setBankSettingsFile = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, paymentTemplateFilePath: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const setLogoFile = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, logoPath: value } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const deleteBankSettingFile = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === value ? { ...el, paymentTemplateFilePath: null } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const deleteLogoFile = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === value ? { ...el, logoPath: null } : el,
			);

			dispatch({
				type: BankSettingsActionsTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const setFetchedAcquirers = useCallback((value: IRestaurantBankSettingsAcquirer[]) => {
		dispatch({
			type: BankSettingsActionsTypes.SET_FETCHED_ACQUIRERS,
			payload: {
				fetchedAcquirers: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IUpdateRestaurantBankSettings | null) => {
		dispatch({
			type: BankSettingsActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: BankSettingsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<BankSettingsContext.Provider
			value={{
				refetchData,
				addBankSettingBlock,
				deleteBankSettingBlock,
				changePriority,
				changeMerchantId,
				changeMerchantName,
				changeAcquirer,
				changeSbpAccountNumber,
				changeSbpLogin,
				changeYandexApiKey,
				changeYandexPaymentType,
				changeVivaApiKey,
				changeVivaApiSecret,
				changePaymentTypeId,
				changeSmsCode,
				changeTBankPlaceId,
				changeMasterWallet,
				toggleDisplayTips,
				toggleIsFeeFiscalized,
				toggleGenerateQrInstantly,
				changeAccNumber,
				changeSector,
				changeSdRef,
				changeYandexChequeApiToken,
				changeYandexChequeApiPartnerId,
				changeMerchantAccount,
				changeStore,
				changeXApiKey,
				changeCheckoutUrlPrefix,
				toggleSplitPayments3S,
				changeMerchantBalanceAccountId,
				changeKmBalanceAccountId,
				setBankSettingsFile,
				setLogoFile,
				deleteBankSettingFile,
				deleteLogoFile,
				setFetchedAcquirers,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				bankSettings: state.bankSettings,
				fetchedAcquirers: state.fetchedAcquirers,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</BankSettingsContext.Provider>
	);
};

export default BankSettingsState;
